import { zodResolver } from "@hookform/resolvers/zod"
import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { Navigate } from "react-router-dom"
import * as z from "zod"
import { gql } from "~/__generated__"
import { UserStatus } from "~/__generated__/graphql"
import { useViewerMaybe } from "~/auth/use-viewer"
import { onboardingVerifyPhonePath } from "~/common/paths"
import { useFormErrors } from "~/common/use-form-errors"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { TextField } from "~/fields/text-field"
import { Button } from "~/ui/button"
import { Form } from "~/ui/form"
import { useToast } from "~/ui/use-toast"

const emailFormSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
})

const verificationFormSchema = z.object({
  token: z.string().min(1, { message: "Verification code is required" }),
})

type EmailFormValues = z.infer<typeof emailFormSchema>
type VerificationFormValues = z.infer<typeof verificationFormSchema>

const USER_EMAIL_CREATE_MUTATION = gql(/* GraphQL */ `
  mutation UserEmailCreate($input: UserEmailCreateInput!) {
    userEmailCreate(input: $input) {
      userEmail {
        id
        email
        status
      }
    }
  }
`)

const USER_EMAIL_VERIFY_MUTATION = gql(/* GraphQL */ `
  mutation UserEmailVerify($input: UserEmailVerifyInput!) {
    userEmailVerify(input: $input) {
      userEmail {
        id
        email
        status
        user {
          id
          userStatus
        }
      }
    }
  }
`)

export const OnboardingVerifyEmailScreen: React.FC = () => {
  const [emailSubmitted, setEmailSubmitted] = useState(false)
  const [userEmailId, setUserEmailId] = useState<string>("")
  const [userEmailCreate, userEmailCreateResult] = useSafeMutation(USER_EMAIL_CREATE_MUTATION)
  const [userEmailVerify, userEmailVerifyResult] = useSafeMutation(USER_EMAIL_VERIFY_MUTATION)
  const { toast } = useToast()
  const {
    viewer,
    result: { refetch: refetchViewer },
  } = useViewerMaybe()

  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: {
      email: "",
    },
  })

  const verificationForm = useForm<VerificationFormValues>({
    resolver: zodResolver(verificationFormSchema),
    defaultValues: {
      token: "",
    },
  })

  useFormErrors(emailForm.setError, userEmailCreateResult)
  useFormErrors(verificationForm.setError, userEmailVerifyResult)

  // Redirect to phone verification if user has phone pending status
  if (viewer?.userStatus === UserStatus.OnboardingPhonePending) {
    return <Navigate to={onboardingVerifyPhonePath({})} replace />
  }

  const onEmailSubmit = async (values: EmailFormValues) => {
    const result = await userEmailCreate({
      variables: {
        input: {
          userEmailInput: {
            email: values.email,
          },
        },
      },
    })

    if (result.errors) {
      return
    }

    setUserEmailId(result.data?.userEmailCreate.userEmail.id || "")
    setEmailSubmitted(true)
    toast({
      title: "Verification Email Sent",
      description: "Please check your email for the verification code.",
      variant: "default",
    })
  }

  const onVerificationSubmit = async (values: VerificationFormValues) => {
    const result = await userEmailVerify({
      variables: {
        input: {
          userEmailId,
          token: values.token,
        },
      },
    })

    if (result.errors) {
      return
    }

    await refetchViewer()
    toast({
      title: "Email Verified",
      description: "Your email has been successfully verified.",
      variant: "default",
    })
  }

  const handleSendAgain = () => {
    setEmailSubmitted(false)
    emailForm.reset()
  }

  if (emailSubmitted) {
    return (
      <div>
        <div className="space-y-2">
          <h1 className="text-xs text-[#777]">Verification</h1>
          <h2 className="text-lg font-medium text-[#333]">Enter verification code</h2>
        </div>
        <hr className="mb-10 mt-6 border-t border-[#EAEAEA]" style={{ height: "0.5px" }} />
        <div className="space-y-6">
          <p className="text-sm text-gray-600">
            A verification code was sent to the email provided. Please check your email and input
            the code provided.
          </p>
          <Form {...verificationForm}>
            <form
              onSubmit={verificationForm.handleSubmit(onVerificationSubmit)}
              className="space-y-6"
            >
              <TextField
                control={verificationForm.control}
                name="token"
                label="Code Input"
                placeholder="000000"
                className="text-center tracking-widest"
              />
              <Button type="submit" className="w-full" disabled={userEmailVerifyResult.loading}>
                {userEmailVerifyResult.loading ? "Verifying..." : "Verify Code"}
              </Button>
            </form>
          </Form>
          <p className="text-sm text-gray-500">
            Didn't receive the code?{" "}
            <button
              type="button"
              onClick={handleSendAgain}
              className="text-blue-600 underline hover:text-blue-800"
            >
              Send Again
            </button>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="space-y-2">
        <h1 className="text-xs text-[#777]">Get Started</h1>
        <h2 className="text-lg font-medium text-[#333]">Verify your email address</h2>
      </div>
      <hr className="mb-10 mt-6 border-t border-[#EAEAEA]" style={{ height: "0.5px" }} />
      <Form {...emailForm}>
        <form onSubmit={emailForm.handleSubmit(onEmailSubmit)} className="space-y-6">
          <TextField
            control={emailForm.control}
            name="email"
            label="Email Address"
            placeholder="Enter your email address"
          />
          <Button type="submit" className="w-full" disabled={userEmailCreateResult.loading}>
            {userEmailCreateResult.loading ? "Sending..." : "Send Verification Email"}
          </Button>
        </form>
      </Form>
    </div>
  )
}
